import { useQueryState } from "nuqs";
import React, { createContext, useContext, useEffect, useState } from "react";
import {
    BusinessCode,
    MicrositePageContentFragment,
    TripBuilderDepartureQuery,
    useTripBuilderDepartureSuspenseQuery,
} from "../../../../generated/graphql";
import {
    TripBuilderDepartureDetailType,
    useTourPageContext,
} from "../../../_client/context";

export type TripBuilderContextValue = {
    departureId: string | null;
    setDepartureId: (departureId: string | null) => void;
    departure?: TripBuilderDepartureQuery["departure"];
    departureDetailType?: TripBuilderDepartureDetailType;
    gcDeparture?: MicrositePageContentFragment["gcDeparture"];
    promoCode: string | null;
    campaignCode: string | null;
};

const TripBuilderContext = createContext<TripBuilderContextValue>(null!);

export type TripBuilderContextProviderProps = {
    gcDeparture?: MicrositePageContentFragment["gcDeparture"];
};
export const TripBuilderContextProvider: React.FC<
    React.PropsWithChildren<TripBuilderContextProviderProps>
> = ({ children, gcDeparture }) => {
    const { pageConfig } = useTourPageContext();
    const [departureIdState, setDepartureId] = useQueryState("departure");
    const departureId = gcDeparture?.departureId || departureIdState;
    const [referenceBtn, setReferenceBtn] = useState<string | null>(null);
    const { data } = useTripBuilderDepartureSuspenseQuery({
        variables: {
            departureId: departureId ?? "",
            language: process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
            businessCode: process.env.NEXT_PUBLIC_BUSINESS_CODE as BusinessCode,
        },
        context: { fetchOptions: { cache: "no-store" } },
        skip: !departureId,
    });

    // TODO: WWB-783:
    // Solve this problem for real, for other codes for other discounts for other tags.
    const promoCode =
        (() => {
            if (gcDeparture) {
                return data?.promoConfigContent?.groupsPromoCode;
            }
            if (
                data?.departure?.tags &&
                data.departure.tags.some(
                    (tag) => tag.name === "Last Minute Special",
                )
            ) {
                return "LastMinuteSpecial";
            }
            return data?.promoConfigContent?.promoCode;
        })() ?? null;

    const campaignCode = gcDeparture
        ? data?.promoConfigContent?.groupsCampaignCode
        : data?.promoConfigContent?.campaignCode;
    useEffect(() => {
        // manage keyboard focus between screens
        if (!departureId && referenceBtn) {
            document.getElementById(referenceBtn)?.focus();
        } else if (departureId && referenceBtn !== `${departureId}_select`) {
            setReferenceBtn(`${departureId}_select`);
        }
    }, [departureId, referenceBtn]);

    return (
        <TripBuilderContext.Provider
            value={{
                departureId,
                setDepartureId,
                departure: gcDeparture?.departure || data?.departure,
                departureDetailType: pageConfig.tripBuilderDepartureDetailType,
                gcDeparture,
                promoCode,
                campaignCode: campaignCode ?? null,
            }}
        >
            {children}
        </TripBuilderContext.Provider>
    );
};

export const useTripBuilder = () => useContext(TripBuilderContext);
